import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "../axios";
import BackButton from "../components/BackButton";
import { translateCategory } from "../utils/categoryTranslations";
import "../styles/clienthistory.css";

const getStatusInfo = (scan) => {
  const st = (scan.scanType === "barcode" && scan.vendorLink?.stock) ? scan.vendorLink.stock : scan.status;
  if (st === "promotion")    return { cls: "ch-status-pill promo",   label: "🏷️ Promotion" };
  if (st === "indisponible") return { cls: "ch-status-pill indispo", label: "🚫 Indisponible" };
  if (st === "en stock")     return { cls: "ch-status-pill stock",   label: "✅ En stock" };
  return null;
};

const GlobalProductPage = () => {
  const { barcode } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [scanContext, setScanContext] = useState(null);
  const [brands, setBrands] = useState(null);
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState(null);
  const [shareMsg, setShareMsg] = useState(null);

  const storedUser = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
  })();
  const userId = storedUser?._id || storedUser?.id;

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    let cancelled = false;
    setProduct(null);
    setScanContext(null);
    setBrands(null);
    setError(null);

    // 1. Global product from DB
    axios.get(`/global-products/barcode/${barcode}`)
      .then(res => { if (!cancelled) setProduct(res.data); })
      .catch(err => {
        if (cancelled) return;
        if (err.response?.status === 404) setError("Produit introuvable pour ce code-barres.");
        else setError("Erreur de chargement.");
      });

    // 2. Scan context from history (to show date, city, price, store, status)
    if (userId) {
      axios.get(`/scans/history/${userId}`)
        .then(res => {
          if (cancelled) return;
          const scans = res.data?.scans || res.data || [];
          const match = scans.find(s => s.barcode === barcode);
          if (match) setScanContext(match);
        })
        .catch(() => {});

      // 3. Subscription state
      axios.get(`/push/by-user/${userId}`)
        .then(res => {
          if (cancelled) return;
          const ids = res.data?.scannedProductIds || [];
          setSubscribed(ids.includes(String(barcode)));
        })
        .catch(err => {
          console.error("[Alerte] échec lecture état initial:", err?.response?.status, err?.message);
        });
    }

    // 4. OFF enrichment (brands + image fallback)
    fetch(`https://world.openfoodfacts.org/api/v0/product/${barcode}.json`)
      .then(r => r.json())
      .then(data => {
        if (cancelled || data.status !== 1 || !data.product) return;
        const p = data.product;

        const rawBrands = p.brands;
        if (rawBrands) setBrands(rawBrands.split(",")[0].trim());

        const img = p.image_front_small_url || p.image_small_url || p.image_front_thumb_url;
        if (img) {
          const secureImg = img.replace(/^http:\/\//i, "https://");
          setProduct(prev => prev && !prev.imageUrl ? { ...prev, imageUrl: secureImg } : prev);
          setProduct(prev => {
            if (!prev || prev.imageUrl) return prev;
            axios.put(`/global-products/${prev._id}`, { imageUrl: secureImg }).catch(() => {});
            return { ...prev, imageUrl: secureImg };
          });
        }

        const name = p.product_name_fr || p.product_name;
        if (name) {
          setProduct(prev => {
            if (!prev || (prev.name && prev.name !== "Unknown")) return prev;
            axios.put(`/global-products/${prev._id}`, { name }).catch(() => {});
            return { ...prev, name };
          });
        }
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [barcode, userId]);

  const handleToggleAlert = async () => {
    if (!userId) { toast.info("Connecte-toi pour activer les alertes."); return; }
    try {
      const newState = !subscribed;
      setSubscribed(newState);
      if (newState) {
        await axios.put("/push/subscribe-product", { userId, productId: barcode });
        toast.success("🔔 Alerte activée ! Tu seras notifié des mises à jour.");
      } else {
        await axios.post("/push/unsubscribe-product", { userId, productId: barcode });
        toast.info("🔕 Alerte désactivée.");
      }
    } catch {
      setSubscribed(s => !s); // rollback
      toast.error("❌ Impossible de changer l'abonnement.");
    }
  };

  const handleShare = async () => {
    if (!scanContext?._id) return;
    try {
      const res = await axios.post(`/scans/${scanContext._id}/share`);
      const url = `https://www.crush-now.fr/c/${res.data.slug}`;
      await navigator.clipboard.writeText(url);
      setShareMsg("Lien copie !");
      setTimeout(() => setShareMsg(null), 3000);
    } catch {
      toast.error("Impossible de generer le lien de partage.");
    }
  };

  const handleDelete = async () => {
    if (!scanContext) return;
    const name = scanContext.articleName || "cet article";
    if (!window.confirm(`❗ Supprimer le scan de "${name}" de tes crushs ?`)) return;
    try {
      await axios.delete(`/scans/${scanContext._id}`);
      toast.success("✅ Scan supprimé !");
      navigate("/client", { state: { tab: "scans" } });
    } catch {
      toast.error("❌ Erreur lors de la suppression.");
    }
  };

  // ── Render ──

  const statusInfo = scanContext ? getStatusInfo(scanContext) : null;
  const effectivePrice = scanContext?.vendorLink?.price ?? scanContext?.price ?? null;
  const effectivePriceSource = effectivePrice && (scanContext?.vendorLink?.price != null ? null : scanContext?.price_source);
  const storeName = scanContext?.location_label || (scanContext?.ownerName && scanContext.ownerName !== "Inconnu" ? scanContext.ownerName : null);

  return (
    <div style={{
      minHeight: "100svh", background: "#f8fafc", padding: "16px 16px 40px",
      fontFamily: "system-ui, -apple-system, sans-serif", maxWidth: "480px", margin: "0 auto"
    }}>
      <BackButton />

      {error && (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
          <div style={{ fontSize: "40px", marginBottom: "12px" }}>📦</div>
          <p>{error}</p>
          <p style={{ fontSize: "12px", marginTop: "4px", fontFamily: "monospace" }}>{barcode}</p>
        </div>
      )}

      {!product && !error && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>Chargement…</div>
      )}

      {product && (
        <>
          {/* ── Bloc identité produit ── */}
          <div style={{
            background: "white", borderRadius: "16px", padding: "20px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.08)", marginBottom: "12px"
          }}>
            {product.imageUrl ? (
              <img
                src={product.imageUrl}
                alt={product.name}
                style={{ width: "100%", maxHeight: "180px", objectFit: "contain",
                         borderRadius: "8px", marginBottom: "16px", border: "1px solid #e2e8f0" }}
              />
            ) : (
              <div style={{ fontSize: "56px", textAlign: "center", marginBottom: "16px" }}>📦</div>
            )}

            <h1 style={{ fontSize: "19px", fontWeight: 700, color: "#1e293b", marginBottom: "6px", lineHeight: 1.3 }}>
              {product.name}
            </h1>

            {brands && (
              <p style={{ fontSize: "13px", color: "#475569", marginBottom: "4px" }}>
                <span style={{ fontWeight: 600 }}>Marque :</span> {brands}
              </p>
            )}

            {product.category && (
              <p style={{ fontSize: "13px", color: "#64748b" }}>
                <span style={{ fontWeight: 600 }}>Catégorie :</span> {translateCategory(product.category)}
              </p>
            )}
          </div>

          {/* ── Bloc contexte du scan ── */}
          {scanContext && (
            <div style={{
              background: "white", borderRadius: "16px", padding: "16px 20px",
              boxShadow: "0 4px 24px rgba(0,0,0,0.06)", marginBottom: "12px"
            }}>
              <p style={{ fontSize: "11px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase",
                          letterSpacing: "0.06em", marginBottom: "10px" }}>
                Contexte du scan
              </p>

              {storeName && (
                <div style={{ fontSize: "14px", color: "#1e293b", marginBottom: "6px" }}>
                  🏪 {storeName}
                </div>
              )}

              {scanContext.city && (
                <div style={{ fontSize: "14px", color: "#1e293b", marginBottom: "6px" }}>
                  📍 {scanContext.city}
                </div>
              )}

              {scanContext.date && (
                <div style={{ fontSize: "14px", color: "#1e293b", marginBottom: "6px" }}>
                  📅 Scanné le {new Date(scanContext.date).toLocaleDateString("fr-FR")}
                </div>
              )}

              {effectivePrice != null && (
                <div style={{ fontSize: "14px", color: "#1e293b", marginBottom: "6px" }}>
                  💰 {effectivePrice}€
                  {effectivePriceSource === "open_prices" && (
                    <span style={{ fontSize: "10px", color: "#94a3b8", marginLeft: "5px" }}>Open Prices</span>
                  )}
                </div>
              )}

              {scanContext.vendorLink?.customNote && (
                <div style={{ fontSize: "12px", color: "#64748b", fontStyle: "italic", marginBottom: "6px" }}>
                  "{scanContext.vendorLink.customNote}"
                </div>
              )}

              {statusInfo && (
                <span className={statusInfo.cls} style={{ marginTop: "4px", display: "inline-block" }}>
                  {statusInfo.label}
                </span>
              )}
            </div>
          )}

          {/* ── Bloc actions ── */}
          <div style={{
            background: "white", borderRadius: "16px", padding: "16px 20px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.06)", marginBottom: "12px",
            display: "flex", flexDirection: "column", gap: "10px"
          }}>
            <button
              onClick={handleToggleAlert}
              style={{
                width: "100%", minHeight: "44px", fontSize: "15px", fontWeight: 700,
                borderRadius: "22px", border: "none", cursor: "pointer",
                background: subscribed ? "#FEE2E2" : "#D1FAE5",
                color: subscribed ? "#991B1B" : "#065F46",
              }}
            >
              {subscribed ? "🔕 Stop alerte" : "🔔 Alerte"}
            </button>

            {scanContext && (
              <button
                onClick={handleShare}
                style={{
                  width: "100%", minHeight: "44px", fontSize: "15px", fontWeight: 700,
                  borderRadius: "22px", border: "none", cursor: "pointer",
                  background: shareMsg ? "#D1FAE5" : "linear-gradient(135deg, #D41A6E, #E87010)",
                  color: shareMsg ? "#065F46" : "white",
                  transition: "background 0.2s",
                }}
              >
                {shareMsg ? shareMsg : "Partager ce crush"}
              </button>
            )}

            {scanContext && (
              <button
                onClick={handleDelete}
                style={{
                  width: "100%", minHeight: "44px", fontSize: "14px", fontWeight: 600,
                  borderRadius: "22px", border: "2px solid #FEE2E2", cursor: "pointer",
                  background: "white", color: "#991B1B",
                }}
              >
                🗑️ Retirer de mes crushs
              </button>
            )}
          </div>

          {/* ── Footer discret ── */}
          <div style={{ padding: "4px 4px 0", textAlign: "center" }}>
            <p style={{ fontSize: "11px", fontFamily: "monospace", color: "#cbd5e1", margin: "0 0 3px" }}>
              Code-barres : {barcode}
            </p>
            {product.source && (
              <p style={{ fontSize: "10px", color: "#e2e8f0", margin: 0 }}>
                Source : {product.source}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default GlobalProductPage;
